export type Option<T> = {
  value: T;
  label: React.ReactNode;
  searchStr?: string;
  displayStr?: string;
};

export function optionIsOptionType<T extends number | string>(
  option: T | Option<T>
): option is Option<T> {
  return typeof option === "object";
}

export function getOptionSearchString<T extends number | string>(
  option: T | Option<T>
) {
  if (optionIsOptionType(option)) {
    if (option.searchStr !== null && option.searchStr !== undefined) {
      return option.searchStr.toLowerCase();
    } else {
      return (typeof option.label === "string"
        ? option.label
        : option.value.toString()
      ).toLowerCase();
    }
  } else {
    return option.toString().toLowerCase();
  }
}

export function getOptionValue<T extends number | string>(
  option: T | Option<T>
) {
  if (optionIsOptionType(option)) {
    return option.value;
  } else {
    return option;
  }
}

interface DisplayProps {
  name?: string;
  /** Label. Will be above the radio buttons */
  label?: React.ReactNode;
  /** Tags that are to be published along with the label */
  labelTags?: React.ReactNode | React.ReactNode[];
  /** Error. Will be below radio buttons, and Help */
  error?: React.ReactNode;
  /** Help. Will be below radio buttons. If error is True, this will also be Red. */
  help?: React.ReactNode;
  /** Is the field required? This is PURELY A VISUAL representation. */
  required?: boolean;
  placeholder?: React.ReactNode;
}

interface OptionProps<T> {
  /** Select Options */
  options: Option<T>[];
}

interface ValueProps<T> {
  /** Select Options */
  allowedValues: T[];
}

interface SingleSelectInteractions<T> {
  /** Current Selection */
  selected: T | null;
  /** OnClick call back */
  onChange: (value: T | null) => any;
}

interface MultiSelectInteractions<T> {
  /** Should the full listing be shown as a grid with checkboxes? */
  fullListing?: boolean;
  /** Current Selection */
  selected: T[];
  /** OnClick call back */
  onChange: (values: T[]) => any;
  /** Should SELECT ALL / DE-SELECT ALL be enabled? Defaults to True. */
  enableSelectAll?: boolean;
}

export interface SingleSelectOptionProps<T>
  extends DisplayProps,
    SingleSelectInteractions<T>,
    OptionProps<T> {}

export interface SingleSelectValuesProps<T>
  extends DisplayProps,
    SingleSelectInteractions<T>,
    ValueProps<T> {}

export type SingleSelectProps<T> = DisplayProps &
  SingleSelectInteractions<T> &
  (OptionProps<T> | ValueProps<T>);

export type MultiSelectProps<T> = DisplayProps &
  MultiSelectInteractions<T> &
  (OptionProps<T> | ValueProps<T>);
